import * as THREE from "three";
import { Loader } from "../../assets/loader";
import { Utils } from "../../../utils/utils";
import AppData from "../../../app.data";
import { Slot } from "@/types/slotsData";
import { ArtworkWrapper } from "@/types/artworksData";
import { ArtworkMedia } from "./media.manager";
import { ArtworkFrame } from "../attachments/frame.attachment";
import { ArtworkLabel } from "../attachments/label.attachment";
import SoundService from "../../../services/sound/sound.service";
import {
  DEFAULT_ARTWORK_MODEL_URL,
  DEFAULT_ARTWORK_MODEL_IMAGE_URL,
  DEBUG_SLOTS
} from "../artwork.constants";
import { resolveArtworkWrapperTransform } from "../../../utils/artworkPlacement.utils";
import { cleanupArtworkRuntimeRegistries } from "../../../utils/runtime-registry.utils";
import { disposeOfChildren } from "../../../utils/resource.utils";
const appData = new AppData()
const PSEUDO_MOM_IMAGE_SCALE_MULTIPLIER = 1.5;
const STANDARD_BLANK_PLACEHOLDER_MAX_DIMENSION = 0.7;
const MOM_GALLERY_SPACE_TYPES = new Set(["mom", "sellermom"]);

export const shouldAddPseudoMoMArtworkLabel = (spaceData: any, artworkWrapper?: ArtworkWrapper | null): boolean => {
  return spaceData?.subtype === "pseudoMoM" && (artworkWrapper?.artworks?.length || 0) > 0;
};

export const shouldShowPseudoMoMArtworkArrows = (spaceData: any, artworkWrapper?: ArtworkWrapper | null): boolean => {
  return spaceData?.subtype === "pseudoMoM" && (artworkWrapper?.artworks?.length || 0) > 1;
};

export class ArtworkHandler {
  private static cleanupStaleLoadedMesh(utils: Utils, loadedMesh: any) {
    cleanupArtworkRuntimeRegistries({ root: loadedMesh });
    utils.disposeOfChildren(loadedMesh);
  }

  private static shouldContainBlankPlaceholder() {
    if (appData.spaceData?.subtype === "pseudoMoM") return false;
    const gallerySpaceType = String(appData.galleryData?.spaceType || "").toLowerCase();
    return !MOM_GALLERY_SPACE_TYPES.has(gallerySpaceType);
  }

  private static containBlankPlaceholder(loadedMesh: any, outputWidth: number, outputHeight: number) {
    if (!this.shouldContainBlankPlaceholder()) return;
    const maxDimension = Math.max(Number(outputWidth), Number(outputHeight));
    if (!Number.isFinite(maxDimension) || maxDimension <= 0) return;

    const scaleFactor = Math.min(1, STANDARD_BLANK_PLACEHOLDER_MAX_DIMENSION / maxDimension);
    loadedMesh.scale.multiplyScalar(scaleFactor);
  }

  /**
   * Creates placeholder artwork holders for empty slots
   * Used in editor mode for space types: hexa, procedural, or custom
   * @param loader Asset loader instance
   * @param parent Parent object to attach artwork to
   * @returns Promise resolving when placeholder is created
   */
  static async addImageHolderForBlankArtworks(
    loader: Loader,
    parent: THREE.Object3D,
    isCurrent: () => boolean = () => true,
    cleanupLoadedMesh?: (loadedMesh: any) => void
  ) {
    let loadedMesh: any = null;
    let loadedMeshCleaned = false;
    const cleanupStalePlaceholder = () => {
      if (!loadedMesh || loadedMeshCleaned) return;
      loadedMeshCleaned = true;
      if (cleanupLoadedMesh) {
        cleanupLoadedMesh(loadedMesh);
      } else {
        cleanupArtworkRuntimeRegistries({ root: loadedMesh });
        disposeOfChildren(loadedMesh);
      }
    };
    const rejectStalePlaceholder = () => {
      if (isCurrent()) return null;
      cleanupStalePlaceholder();
      return {
        attached: false,
        staleCleanupCount: loadedMesh ? 1 : 0,
      };
    };

    try {
      loadedMesh = await loader.loadGLTF(DEFAULT_ARTWORK_MODEL_URL, true);
      const staleAfterModelLoad = rejectStalePlaceholder();
      if (staleAfterModelLoad) return staleAfterModelLoad;

      loadedMesh.traverse((child) => {
        child.userData = { ...parent.userData, artworkWrapper: null } as Slot
      });
      loadedMesh.userData = { ...parent.userData, artworkWrapper: null } as Slot
      const artwork = loadedMesh.getObjectByName("artwork");
      if (artwork) {
        const { outputWidth, outputHeight } = await ArtworkMedia.loadNFTImage(loader, artwork, DEFAULT_ARTWORK_MODEL_IMAGE_URL);
        const staleAfterImageLoad = rejectStalePlaceholder();
        if (staleAfterImageLoad) return staleAfterImageLoad;
        this.containBlankPlaceholder(loadedMesh, outputWidth, outputHeight);
        if (DEBUG_SLOTS) {
          // Create a canvas for the texture
          const canvas = document.createElement('canvas');
          canvas.width = 512;
          canvas.height = 512;
          const context = canvas.getContext('2d');

          if (context) {
            // Draw random number
            context.fillStyle = '#000000';
            context.font = 'bold 80px Arial';
            context.textAlign = 'center';
            context.textBaseline = 'middle';

            context.fillText(String(parent.userData.index), canvas.width / 2, canvas.height / 2);

            // Create texture from canvas
            const canvasTexture = new THREE.CanvasTexture(canvas);
            canvasTexture.needsUpdate = true;

            // Create a plane geometry (1x1)
            const planeGeometry = new THREE.PlaneGeometry(1.5, 1.5);
            const planeMaterial = new THREE.MeshBasicMaterial({
              map: canvasTexture,
              side: THREE.DoubleSide,
              alphaTest: 0.5
            });

            // Create mesh and position it vertically
            const plane = new THREE.Mesh(planeGeometry, planeMaterial);
            plane.userData = { ...parent.userData }
            plane.position.set(0, 0.25, 0.05); // Position above the artwork
            plane.rotation.y = 0; // Face forward

            // Add to the loaded mesh
            loadedMesh.add(plane);
            if (isCurrent()) {
              appData.renderService?.addToRenderQueue?.();
            }
          }
        }

        const staleBeforeAttach = rejectStalePlaceholder();
        if (staleBeforeAttach) return staleBeforeAttach;
        parent.add(loadedMesh)
        return { attached: true };
      }

      cleanupStalePlaceholder();
      return { attached: false };
    } catch (error) {
      cleanupStalePlaceholder();
      console.error("ArtworkHandler: Error in addImageHolderForBlankArtworks", error);
      throw error;
    }
  }

  /**
   * Loads artworks by proximity, attaching them as soon as their preload finishes.
   * Uses small gaps between adds (after the first) to avoid frame spikes.
   */
  static async loadArtworksInDistanceOrder(
    utils: Utils,
    appData: AppData,
    preloadArtwork: (artworkWrapper: ArtworkWrapper) => Promise<{ loadedMesh: any, imageDioaram: string, activeArtworkIndex?: number } | null>,
    loadArtworkInSlot: (artworkWrapper: ArtworkWrapper, slotId: number, preloadData?: { loadedMesh: any, imageDioaram: string, activeArtworkIndex?: number } | null) => Promise<any>,
    isCurrent: () => boolean = () => true,
    cleanupPreload?: (preloadData: { loadedMesh: any, imageDioaram: string, activeArtworkIndex?: number }) => void
  ) {
    try {
      const playerWorldPosition =
        appData.physicsService?.playerCollider?.end?.clone?.() ||
        appData.camera?.position?.clone?.() ||
        new THREE.Vector3();
      const sortedArtworkWrappers = utils.sortByDistanceToCamera(appData.galleryData.artworkWrappers, playerWorldPosition);

      // Simple sequential pipeline: preload then place, one after another, with a tiny gap.
      for (let i = 0; i < sortedArtworkWrappers.length; i++) {
        if (!isCurrent()) break;
        const artworkWrapper = sortedArtworkWrappers[i];
        let preloadData: { loadedMesh: any, imageDioaram: string } | null = null;

        try {
          preloadData = await preloadArtwork(artworkWrapper);
        } catch (error) {
          console.error(`ArtworkHandler: Error preloading artwork slotTarget ${artworkWrapper?.slotTarget}`, error);
          preloadData = null;
        }

        if (!isCurrent()) {
          if (preloadData) cleanupPreload?.(preloadData);
          break;
        }

        try {
          await loadArtworkInSlot(artworkWrapper, artworkWrapper.slotTarget, preloadData);
          if (!isCurrent()) break;
          appData.renderService.addToRenderQueue()
        } catch (error) {
          console.error(`ArtworkHandler: Error loading artwork in slot target ${artworkWrapper.slotTarget}:`, error);
        }

        // brief pause between sequential adds to reduce spikes; skip after last
        if (i < sortedArtworkWrappers.length - 1) {
          await ArtworkHandler.delay(15);
          if (!isCurrent()) break;
        }
      }

      return {};
    } catch (error) {
      console.error("ArtworkHandler: Error in loadArtworksInDistanceOrder", error);
      throw error;
    }
  }

  /**
   * Wait helper to pause execution between heavy loads
   */
  static delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  static async handleSoundArtworkSlot(utils: Utils, appData: AppData, soundService: SoundService, artworkWrapper: ArtworkWrapper, loadedMesh: any, slotId: number, isLoadCurrent?: () => boolean) {
    let ownedSound: any = null;
    const cleanupOwnedSound = () => {
      if (!ownedSound) return;
      const sound = ownedSound;
      ownedSound = null;
      soundService.cleanupRuntimeSound?.(sound);
      appData.soundList = (appData.soundList || []).filter((entry: any) => entry !== sound);
    };

    try {
      const slot = utils.getSlotObjectBySlotTarget(slotId, appData)
      if (slot && slot.visible) {
        if (isLoadCurrent && !isLoadCurrent()) {
          this.cleanupStaleLoadedMesh(utils, loadedMesh);
          return { attached: false, staleCleanupCount: 1 };
        }

        const placement = resolveArtworkWrapperTransform(artworkWrapper, appData.spaceData);
        const soundPlacementWrapper = {
          ...artworkWrapper,
          transforms: placement.transform,
        };
        const sound = await soundService.loadSoundInSlot(slot, artworkWrapper.artworks[artworkWrapper.activeArtwork].artworkData, soundPlacementWrapper, loadedMesh);
        ownedSound = sound;
        if (isLoadCurrent && !isLoadCurrent()) {
          cleanupOwnedSound();
          this.cleanupStaleLoadedMesh(utils, loadedMesh);
          return { attached: false, staleCleanupCount: 1 };
        }
        utils.disposeOfChildren(slot, true)

        loadedMesh.position.x += placement.transform.position.x
        loadedMesh.position.y += placement.transform.position.y
        loadedMesh.position.z += placement.transform.position.z

        if (placement.transform.rotation) {
          loadedMesh.rotation.x = placement.transform.rotation.x
          loadedMesh.rotation.y = placement.transform.rotation.y
          loadedMesh.rotation.z = placement.transform.rotation.z
        }

        // @ts-ignore
        artworkWrapper.artworks[artworkWrapper.activeArtwork].artworkData.sound = sound
        // @ts-ignore
        artworkWrapper.artworks[artworkWrapper.activeArtwork].artworkData.isPlaying = false
        loadedMesh.traverse((child: any) => {

          child.userData = { ...slot.userData, artworkWrapper: artworkWrapper } as Slot
          // hide pause button
          if (child.name === "button_pause") {
            child.visible = false
          }
        })
        const height = artworkWrapper.artworks[artworkWrapper.activeArtwork].artworkData.height / 100
        if (height) {
          const heightFactor = height / 0.7491
          loadedMesh.scale.x = heightFactor
          loadedMesh.scale.y = heightFactor
          loadedMesh.scale.z = heightFactor
        }
        loadedMesh.userData = { ...slot.userData, artworkWrapper: artworkWrapper } as Slot

        slot.add(loadedMesh)
        appData.soundList.push(sound)
        ownedSound = null;
        return { attached: true };
      }
      this.cleanupStaleLoadedMesh(utils, loadedMesh);
      return { attached: false };
    } catch (error) {
      try {
        cleanupOwnedSound();
      } finally {
        this.cleanupStaleLoadedMesh(utils, loadedMesh);
      }
      console.error("ArtworkHandler: Error in handleSoundArtworkSlot", error);
      throw error;
    }
  }

  /**
   * Handles setup of image-type artwork in a slot
   * Manages texture loading and material configuration
   * @param loader Asset loader instance
   * @param utils Utils instance
   * @param appData AppData instance
   * @param artworkWrapper Artwork configuration
   * @param loadedMesh Loaded 3D mesh
   * @returns Promise resolving when setup is complete
   */
  static async handleImageArtworkSlot(loader: Loader, utils: Utils, appData: AppData, artworkWrapper: ArtworkWrapper, loadedMesh: any, slotId: number, isLoadCurrent?: () => boolean) {
    try {
      const activeArtwork = artworkWrapper.artworks[artworkWrapper.activeArtwork]
      const artworkModel = loadedMesh.getObjectByName(activeArtwork.artworkData.meshName);
      let artworkURL = activeArtwork.artworkData.dataUrl
      if (artworkModel) {
        const { outputWidth, outputHeight } = await ArtworkMedia.loadNFTGraphics(loader, utils, artworkModel, artworkURL, artworkWrapper);
        if (isLoadCurrent && !isLoadCurrent()) {
          this.cleanupStaleLoadedMesh(utils, loadedMesh);
          return { attached: false, staleCleanupCount: 1 };
        }
        const slot = utils.getSlotObjectBySlotTarget(slotId, appData)
        if (slot && slot.visible) {
          if (isLoadCurrent && !isLoadCurrent()) {
            this.cleanupStaleLoadedMesh(utils, loadedMesh);
            return { attached: false, staleCleanupCount: 1 };
          }
          const isPseudoMoM = appData.spaceData?.subtype === "pseudoMoM";
          if (isPseudoMoM) {
            slot.scale.set(1, 1, 1)
          }
          const frameScaleMultiplier = isPseudoMoM ? 0.7 : 1;
          const frameWidth = outputWidth * frameScaleMultiplier;
          const frameHeight = outputHeight * frameScaleMultiplier;
          switch (activeArtwork.artworkData.frame.type) {
            case "frame1":
              ArtworkFrame.scaleFrame1(loadedMesh, frameWidth, frameHeight, activeArtwork.artworkData.frame.thickness, activeArtwork.artworkData.frame.color)
              break;
            case "frame1_pprt":
              ArtworkFrame.scaleFrame1_pprt(loadedMesh, frameWidth, frameHeight, activeArtwork.artworkData.frame.thickness, activeArtwork.artworkData.frame.color, activeArtwork.artworkData.frame.pasPartoutSize)
              break;
          }
          if (isPseudoMoM) {
            loadedMesh.scale.multiplyScalar(PSEUDO_MOM_IMAGE_SCALE_MULTIPLIER);
          }

          utils.disposeOfChildren(slot, true);
          const placement = resolveArtworkWrapperTransform(artworkWrapper, appData.spaceData);
          loadedMesh.position.x += placement.transform.position.x
          loadedMesh.position.y += placement.transform.position.y
          loadedMesh.position.z += placement.transform.position.z

          if (placement.transform.rotation) {
            loadedMesh.rotation.x = placement.transform.rotation.x
            loadedMesh.rotation.y = placement.transform.rotation.y
            loadedMesh.rotation.z = placement.transform.rotation.z
          }

          loadedMesh.traverse((child: any) => {
            child.userData = { ...slot.userData, artworkWrapper: artworkWrapper } as Slot
          })
          loadedMesh.userData = { ...slot.userData, artworkWrapper: artworkWrapper } as Slot

          slot.userData = loadedMesh.userData
          slot.add(loadedMesh);
          if (shouldAddPseudoMoMArtworkLabel(appData.spaceData, artworkWrapper)) {
            const sprite: any = await ArtworkLabel.addArtworkLabel(loader, artworkWrapper, {
              layout: "pseudoMoMFixed",
              showArrows: shouldShowPseudoMoMArtworkArrows(appData.spaceData, artworkWrapper),
            });
            if (isLoadCurrent && !isLoadCurrent()) {
              utils.disposeOfChildren(sprite);
              this.cleanupStaleLoadedMesh(utils, loadedMesh);
              return { attached: false, staleCleanupCount: 1 };
            }
            slot.add(sprite);
          }
          return { attached: true };
        } else {
          this.cleanupStaleLoadedMesh(utils, loadedMesh);
        }
        return { attached: false };
      }
      this.cleanupStaleLoadedMesh(utils, loadedMesh);
      return { attached: false };
    } catch (error) {
      this.cleanupStaleLoadedMesh(utils, loadedMesh);
      console.error("ArtworkHandler: Error in handleImageArtworkSlot", error);
      throw error;
    }
  }
}
